import { useState, useRef, useEffect } from "react";
import { HelpCircle, ChevronDown } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";

interface TourHelpButtonProps {
  onStartTour: () => void;
}

const PAGE_LABELS: Record<string, { en: string; ar: string }> = {
  "/": { en: "Home", ar: "الرئيسية" },
  "/campaign": { en: "Campaign", ar: "الحملة" },
  "/client-dashboard": { en: "Dashboard", ar: "لوحة التحكم" },
  "/partners": { en: "Partners", ar: "الشركاء" },
  "/influencers": { en: "Influencers", ar: "المؤثرون" },
};

export const TourHelpButton = ({ onStartTour }: TourHelpButtonProps) => {
  const [showNudge, setShowNudge] = useState(false);
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { language } = useLanguage();
  const location = useLocation();

  const pageLabel = PAGE_LABELS[location.pathname];
  const isAr = language === "ar";

  // Idle nudge timer — reset on route change
  useEffect(() => {
    setShowNudge(false);
    if (idleTimer.current) clearTimeout(idleTimer.current);

    if (pageLabel) {
      idleTimer.current = setTimeout(() => {
        setShowNudge(true);
      }, 15000);
    }

    return () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [location.pathname, pageLabel]);


  if (!pageLabel || location.pathname === "/media-owner-dashboard") return null;

  return (
    <div className="fixed bottom-6 right-6 z-[9990] flex flex-col items-end" data-tour="tour-help-button">
      {/* Nudge pill */}
      {showNudge && (
        <div className="mb-3 animate-bounce pointer-events-none drop-shadow-[0_0_12px_hsl(var(--primary))] max-w-[calc(100vw-2rem)]">
          <div className="relative bg-primary text-primary-foreground rounded-full px-4 py-2 text-sm font-medium flex items-center gap-1.5">
            {isAr ? "تحتاج مساعدة؟" : "Need help?"}
            <ChevronDown className="h-4 w-4 animate-pulse" />
            {/* Triangle caret pointing to button center */}
            <div className="absolute -bottom-2 right-4 w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-t-[8px] border-t-primary" />
          </div>
        </div>
      )}

      <button
        onClick={() => { setShowNudge(false); onStartTour(); }}
        className="h-12 w-12 rounded-full bg-primary text-primary-foreground shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 flex items-center justify-center"
        aria-label={isAr ? "مساعدة" : "Help"}
      >
        <HelpCircle className="h-5 w-5" />
      </button>
    </div>
  );
};
